import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from './portalControl.module.scss';

type Props = {
	children: React.ReactNode
	containerRef: React.MutableRefObject<HTMLDivElement | null>
}

export const PortalControl = ({ children, containerRef }: Props) => {
	const portalRef = useRef<HTMLDivElement>(null);
	const [style, setStyle] = useState<React.CSSProperties>();

	const calculateStyle = useCallback(
		() => {
			const container = containerRef.current;

			if (container) {
				const { top, height, width, left } = container.getBoundingClientRect();
				const dx = 60;
				const childrenHeight = portalRef.current?.clientHeight || 0;
				const dy = (childrenHeight - height) / 2;

				const newStyle: React.CSSProperties = {
					width: width + 2 * dx,
					height: childrenHeight,
					left: `${left - dx + window.scrollX}px`,
					top: `${top - dy + window.scrollY}px`,
					bottom: `${top + childrenHeight + window.scrollY}px`,
					overflow: 'unset'
				};

				setStyle(newStyle);
			}
		},
		[containerRef]
	)

	useLayoutEffect(
		() => {
			const container = containerRef.current;
			if (container) {
				const { top, bottom, height, width, left } = container.getBoundingClientRect();
				const newStyle: React.CSSProperties = {
					width: width,
					height: height,
					left: `${left}px`,
					top: `${top}px`,
					bottom: `${bottom}px`,
					overflow: 'hidden'
				};

				setStyle(newStyle);
			}

			setTimeout(calculateStyle, 1);

			window.addEventListener('scroll', calculateStyle, true);

			return () => {
				window.removeEventListener('scroll', calculateStyle, true);
			}
		},
		[calculateStyle, containerRef]
	)

	const component = (
		<div ref={portalRef} className={styles.container} style={style}>
			{children}
		</div>
	)

	return createPortal(component, document.body);
}
